import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Loader } from "./Loader";

/**
 * OJO — hallazgo de la migración: hoy `globals.css` fuerza TODOS los
 * botones habilitados a naranja sólido + texto blanco sin importar el
 * `variant`/`color` que traiga el código fuente:
 *
 *   .mantine-Button-root:not(:disabled):not([data-disabled]) {
 *     --button-bg: var(--mantine-color-multiva-6) !important; ...
 *   }
 *
 * Es decir, un `variant="default"` (pensado para acciones secundarias como
 * "Cancelar"/"Volver") se ve HOY idéntico a uno `variant="filled"`. Acá se
 * decidió NO replicar ese "martillazo" y en cambio diferenciar variantes de
 * verdad (mejor jerarquía visual, y es lo que el nombre de la prop promete)
 * — si el usuario prefiere el look uniforme de hoy, es una línea a ajustar
 * en `buttonVariants` (quitar los estilos por variante y dejar todos con la
 * clase de `filled`).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ease-out-quint duration-140 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer",
  {
    variants: {
      variant: {
        filled: "bg-multiva-600 text-white hover:bg-multiva-700",
        default:
          "bg-background text-foreground border border-border hover:bg-muted",
        outline:
          "bg-transparent text-multiva-600 border border-multiva-600 hover:bg-multiva-50 dark:hover:bg-multiva-900/40",
        light: "bg-multiva-50 text-multiva-800 hover:bg-multiva-100 dark:bg-multiva-900/40 dark:text-multiva-200",
        subtle: "bg-transparent text-multiva-600 hover:bg-multiva-50 dark:hover:bg-multiva-900/40",
        transparent: "bg-transparent text-multiva-600 hover:bg-transparent",
      },
      size: {
        xs: "h-7 px-2.5 text-xs",
        sm: "h-8 px-3.5 text-sm",
        md: "h-9 px-4 text-sm",
        lg: "h-10 px-5 text-base",
        xl: "h-12 px-6 text-lg",
      },
    },
    defaultVariants: { variant: "filled", size: "sm" },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  /** Deshabilita + muestra un spinner en vez del contenido de la izquierda. */
  loading?: boolean;
  fullWidth?: boolean;
}

/** Reemplazo de `<Button>` de Mantine. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      leftSection,
      rightSection,
      loading,
      fullWidth,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={props.type ?? "button"}
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        className
      )}
      {...props}
    >
      {loading ? <Loader size="xs" /> : leftSection}
      {children}
      {!loading && rightSection}
    </button>
  )
);
Button.displayName = "Button";
